import { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2, History } from "lucide-react";
import { useTenant } from "@/context/tenant-context";
import {
  workflowsApi,
  workflowRunsApi,
  type WorkflowRunRow,
} from "@/api/workflows";
import { queryKeys } from "@/lib/query-keys";
import { RunStatusBadge } from "@/components/agent/agent-workflows-tab";
import { withWorkspaceContext } from "@/lib/workspace-navigation";

const STATUS_FILTERS = [
  "all",
  "running",
  "awaiting",
  "completed",
  "failed",
  "cancelled",
] as const;

/**
 * Per-workflow runs list. Reachable from the canvas page's "Runs" button
 * and from the agent Workflows tab. Auto-refreshes every 5s so a kicked-
 * off run appears without a manual reload. Filterable by status.
 */
export function WorkflowRunsList() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] =
    useState<(typeof STATUS_FILTERS)[number]>("all");

  const { data: workflow } = useQuery({
    queryKey: queryKeys.workflows.detail(workflowId ?? ""),
    queryFn: () => workflowsApi.get(workflowId!),
    enabled: !!workflowId,
  });

  const { data: runs, isLoading } = useQuery({
    queryKey: [
      ...queryKeys.workflowRuns.byWorkflow(workflowId ?? ""),
      statusFilter,
    ],
    queryFn: () =>
      workflowRunsApi.listForWorkflow(
        workflowId!,
        100,
        statusFilter === "all" ? undefined : statusFilter,
      ),
    enabled: !!workflowId,
    refetchInterval: 5_000,
  });

  if (!workflowId) return null;

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* ── Top bar ───────────────────────────────────────────────── */}
      <div className="flex shrink-0 items-center gap-3 border-b border-border bg-surface px-4 py-2">
        <button
          onClick={() =>
            navigate(
              withWorkspaceContext(
                `/tenants/${tenantId}/workflows/${workflowId}`,
                searchParams,
              ),
            )
          }
          className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
          aria-label="Back to canvas"
        >
          <ArrowLeft size={14} />
        </button>
        <div className="flex flex-1 items-center gap-2 text-sm">
          <History size={14} className="text-text-muted" />
          <span className="font-semibold text-text-primary">
            {workflow?.name ?? "Workflow"}
          </span>
          <span className="text-text-muted">/ runs</span>
        </div>
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as (typeof STATUS_FILTERS)[number])
          }
          className="rounded-md border border-border bg-surface px-2 py-1 text-xs text-text-secondary"
          aria-label="Filter by status"
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {s === "all" ? "All statuses" : s}
            </option>
          ))}
        </select>
      </div>

      {/* ── Body ──────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <Loader2 size={18} className="animate-spin text-text-muted" />
        ) : !runs || runs.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface/50 py-14 text-center">
            <History size={28} className="mb-3 text-text-muted" />
            <p className="text-sm font-medium text-text-primary">
              {statusFilter === "all" ? "No runs yet" : `No ${statusFilter} runs`}
            </p>
            <p className="mt-1 max-w-xs text-xs text-text-muted">
              {statusFilter === "all"
                ? "Use the Run button on the canvas to kick one off, or trigger it on a schedule or webhook."
                : "Try a different status filter."}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-border bg-surface">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                  <th className="px-3 py-2 text-left">Run</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Trigger</th>
                  <th className="px-3 py-2 text-left">Agent</th>
                  <th className="px-3 py-2 text-left">Started</th>
                  <th className="px-3 py-2 text-left">Duration</th>
                  <th className="px-3 py-2 text-left">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <RunRow
                    key={r.id}
                    run={r}
                    onClick={() =>
                      navigate(
                        withWorkspaceContext(
                          `/tenants/${tenantId}/workflows/${workflowId}/runs/${r.id}`,
                          searchParams,
                        ),
                      )
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function RunRow({
  run,
  onClick,
}: {
  run: WorkflowRunRow;
  onClick: () => void;
}) {
  const duration = formatDuration(run.startedAt, run.completedAt);
  const outcome =
    run.status === "failed"
      ? run.error?.message ?? "failed"
      : run.status === "awaiting"
        ? `awaiting ${run.awaitingType ?? "event"}`
        : run.status === "running" && run.retryAt
          ? "retry pending"
          : run.status === "completed"
            ? "✓"
            : "—";
  return (
    <tr
      className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-hover"
      onClick={onClick}
    >
      <td className="px-3 py-2 font-mono text-[11px] text-text-secondary">
        {run.id.slice(0, 8)}
      </td>
      <td className="px-3 py-2">
        <RunStatusBadge status={run.status} />
      </td>
      <td className="px-3 py-2">
        {run.triggerSource ? (
          <span className="rounded bg-surface-hover px-1.5 py-0.5 text-[11px] text-text-secondary">
            {run.triggerSource}
          </span>
        ) : (
          <span className="text-xs text-text-muted">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-text-secondary">
        {run.assignedAgentName ?? "—"}
      </td>
      <td className="px-3 py-2 text-xs text-text-muted">
        {new Date(run.startedAt).toLocaleString()}
      </td>
      <td className="px-3 py-2 text-xs text-text-muted">{duration}</td>
      <td
        className="max-w-[16rem] truncate px-3 py-2 text-xs text-text-muted"
        title={typeof outcome === "string" ? outcome : undefined}
      >
        {outcome}
      </td>
    </tr>
  );
}

function formatDuration(startedAt: string, completedAt: string | null): string {
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const ms = end - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3_600_000) {
    const mins = Math.floor(ms / 60_000);
    const secs = Math.floor((ms % 60_000) / 1000);
    return `${mins}m ${secs}s`;
  }
  const hrs = Math.floor(ms / 3_600_000);
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  return `${hrs}h ${mins}m`;
}
